import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { getErrorMessage } from "@/lib/errors";
import { formatEuro } from "@/components/Badges";
import { plural } from "@/lib/plural";
import { Page } from "@/components/PageConsole";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft } from "@/components/icons";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const firstOfYear = () => `${new Date().getFullYear()}-01-01`;
const today = () => new Date().toISOString().slice(0, 10);

export default function FactuurNieuw() {
  const navigate = useNavigate();
  const [entities, setEntities] = useState([]);
  const [entityId, setEntityId] = useState("");  const [van, setVan] = useState(firstOfYear());
  const [tot, setTot] = useState(today());
  const [cand, setCand] = useState(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/facturen/debiteuren").then(({ data }) => setEntities(data)).catch(() => {});
  }, []);

  const loadCandidates = async () => {
    if (!entityId) { toast.error("Kies eerst een debiteur."); return; }
    setLoading(true); setCand(null);
    try {
      const { data } = await api.get("/facturen/candidates", { params: { entity_id: entityId, van, tot } });
      setCand(data);
    } catch (e) { toast.error(getErrorMessage(e, "Kan werkbonnen niet ophalen.")); }
    finally { setLoading(false); }
  };

  const create = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/facturen", { entity_id: entityId, periode_van: van, periode_tot: tot });
      toast.success("Concept aangemaakt");
      navigate(`/facturen/${data.id}`);
    } catch (e) { toast.error(getErrorMessage(e, "Aanmaken mislukt.")); }
    finally { setCreating(false); }
  };

  return (
    <Page console={{ variant: "view", title: "Nieuwe factuur", backSlot: (<button data-testid="factuur-nieuw-back-button" className="-ml-1 text-white" onClick={() => navigate("/facturen")}><ArrowLeft size={18} /></button>) }}>
      <div className="space-y-4">
        <Card className="p-5">
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label>Debiteur</Label>
              <Select value={entityId} onValueChange={setEntityId}>
                <SelectTrigger data-testid="factuur-entity-select"><SelectValue placeholder="Kies een debiteur" /></SelectTrigger>
                <SelectContent>{entities.map((e) => (
                  <SelectItem key={e.entity_id} value={e.entity_id}>
                    {e.name}{e.locatie_namen?.length ? ` — ${e.locatie_namen.join(", ")}` : ""}{e.is_afzender ? " (eigen BV)" : ""}
                  </SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5"><Label>Periode van</Label><Input type="date" data-testid="factuur-van-input" value={van} onChange={(e) => setVan(e.target.value)} /></div>
            <div className="space-y-1.5"><Label>Periode tot</Label><Input type="date" data-testid="factuur-tot-input" value={tot} onChange={(e) => setTot(e.target.value)} /></div>
          </div>
          <div className="mt-3"><Button data-testid="factuur-zoek-button" onClick={loadCandidates} disabled={loading}>{loading ? "Bezig..." : "Werkbonnen zoeken"}</Button></div>
        </Card>

        {cand && (
          <>
            <Card className="p-5" data-testid="factuur-candidates">
              <h2 className="mb-2 text-body font-semibold text-foreground">Op de factuur ({cand.factureerbaar.length})</h2>
              {cand.factureerbaar.length === 0 ? (
                <p className="text-body text-muted-foreground" data-testid="factuur-candidates-empty">
                  {(cand.zonder_debiteur || []).length > 0
                    ? `Geen werkbonnen voor deze debiteur in deze periode — ${plural(cand.zonder_debiteur.length, "werkbon mist", "werkbonnen missen")} nog een debiteur, zie hieronder.`
                    : "Geen factureerbare werkbonnen in deze periode."}
                </p>
              ) : (
                <ul className="divide-y divide-border">
                  {cand.factureerbaar.map((b) => (
                    <li key={b.werkbon_id} className="flex items-baseline justify-between gap-3 py-2" data-testid={`factuur-cand-${b.werkbon_id}`}>
                      <span className="min-w-0"><span className="font-mono text-foreground">{b.werkbon_number}</span> — {b.omschrijving}{b.location_name ? ` (${b.location_name})` : ""}</span>
                      <span className="font-mono tabular-nums text-foreground">{formatEuro(b.subtotaal_ex_btw)}</span>
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-3 space-y-1 border-t border-lijn-zacht pt-3 text-body">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotaal ex btw</span><span className="font-mono tabular-nums">{formatEuro(cand.subtotaal_ex_btw)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">BTW {cand.btw_percentage}%</span><span className="font-mono tabular-nums">{formatEuro(cand.btw_bedrag)}</span></div>
                <div className="flex justify-between font-semibold text-foreground"><span>Totaal incl. btw</span><span className="font-mono tabular-nums" data-testid="factuur-cand-totaal">{formatEuro(cand.totaal_incl_btw)}</span></div>
              </div>
            </Card>

            {cand.geblokkeerd.length > 0 && (
              <Card className="p-5" data-testid="factuur-geblokkeerd">
                <h2 className="mb-2 text-body font-semibold text-foreground">Kan nog niet op de factuur ({cand.geblokkeerd.length})</h2>
                <ul className="space-y-2">
                  {cand.geblokkeerd.map((g) => (
                    <li key={g.werkbon_id} className="flex flex-wrap items-baseline justify-between gap-2" data-testid={`factuur-geblokkeerd-${g.werkbon_id}`}>
                      <span><span className="font-mono text-foreground">{g.werkbon_number}</span> — {g.omschrijving}</span>
                      <span className="text-data text-letop-tekst">{g.reden === "tarief ontbreekt" ? "tarief ontbreekt — " : "materiaalprijs ontbreekt — "}
                        <button className="underline" data-testid={`factuur-naar-tarief-${g.werkbon_id}`} onClick={() => navigate("/instellingen/tarieven")}>tarief instellen</button>
                      </span>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            {(cand.zonder_debiteur || []).length > 0 && (
              <Card className="p-5" data-testid="factuur-zonder-debiteur">
                <h2 className="mb-2 text-body font-semibold text-foreground">
                  Kan nog niet op een factuur — debiteur ontbreekt ({cand.zonder_debiteur.length})
                </h2>
                <p className="mb-2 text-data text-muted-foreground">
                  Deze werkbonnen zijn gereed en factureerbaar, maar hangen aan geen enkele debiteur. Kies er een op de bon.
                </p>
                <ul className="space-y-2">
                  {cand.zonder_debiteur.map((g) => (
                    <li key={g.werkbon_id} className="flex flex-wrap items-baseline justify-between gap-2" data-testid={`factuur-zonder-debiteur-${g.werkbon_id}`}>
                      <span><span className="font-mono text-foreground">{g.werkbon_number}</span> — {g.omschrijving}{g.location_name ? ` (${g.location_name})` : ""}</span>
                      <button className="text-data text-letop-tekst underline" data-testid={`factuur-naar-debiteur-${g.werkbon_id}`} onClick={() => navigate(`/werkbonnen/${g.werkbon_id}?tab=kosten`)}>
                        debiteur kiezen
                      </button>
                    </li>
                  ))}
                </ul>
              </Card>
            )}

            <div>
              <Button data-testid="factuur-concept-button" onClick={create} disabled={creating || cand.factureerbaar.length === 0}>
                {creating ? "Bezig..." : "Concept aanmaken"}
              </Button>
            </div>
          </>
        )}
      </div>
    </Page>
  );
}
